import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
  Activity,
  AlertTriangle,
  ArrowUpDown,
  BatteryCharging,
  CheckCircle2,
  ChevronDown,
  Cpu,
  ExternalLink,
  Gauge,
  HardDrive,
  Layers,
  Search,
  SlidersHorizontal,
  Star,
  Thermometer,
  Waves,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { useSiteRealtime, useRealtimeStore } from '@/features/realtime/realtimeStore';
import { api } from '@/lib/axios';
import { useSiteSensors } from '@/features/sensors/hooks';
import { useZones } from '@/features/zones/hooks';
import { useBoards } from '@/features/boards/hooks';
import { useTelemetrySummary } from '@/features/telemetry/hooks';
import { useAckAlert, useAlerts, useZoneSummary } from '@/features/alerts/hooks';
import type { Alert as AlertRow, ZoneSummaryRow } from '@monitor/shared';
import { useTheme } from '@/features/theme/useTheme';
import { CHART_THEMES } from '@/features/theme/chartTheme';
import {
  buildCompareOption,
  axisTicks,
  CHART_TICK_MS,
  SPAN_STEPS,
  type ChartWindow,
} from '@/features/dashboard/compareChart';
import { dayjs } from '@/lib/dayjs';
import { boardStatusOf, type BoardStatus } from '@/lib/boardStatus';
import { BoardStatusBadge } from '@/components/BoardStatusBadge';
import type { BoardWithSensors, SensorWithContext, TelemetryPoint } from '@monitor/shared';

// ────────────────────────────────────────────────────────────────
//  Freshness model
//  Spec:
//    realtime  : 0–10 s
//    delayed   : 10–30 s
//    stale     : 30 s – 2 m
//    offline   : > 2 m
// ────────────────────────────────────────────────────────────────
type Freshness = 'fresh' | 'delayed' | 'stale' | 'offline' | 'never';
const FRESHNESS_BUCKETS: Array<{ max: number; state: Freshness }> = [
  { max: 10_000,   state: 'fresh'    },
  { max: 30_000,   state: 'delayed'  },
  { max: 120_000,  state: 'stale'    },
  { max: Infinity, state: 'offline'  },
];
function freshness(timeMs?: number | null, now = Date.now()): Freshness {
  if (timeMs == null) return 'never';
  const age = now - timeMs;
  for (const b of FRESHNESS_BUCKETS) if (age < b.max) return b.state;
  return 'offline';
}
function freshnessColor(f: Freshness): string {
  return f === 'fresh' ? 'var(--green)'
       : f === 'delayed' ? 'var(--yellow)'
       : f === 'stale' ? 'var(--yellow)'
       : f === 'never' ? '#64748b'
       : 'var(--red)';
}

// ────────────────────────────────────────────────────────────────
//  Sensor type classification
//  Firmware emits one of these models on the sensor record. The UI
//  only switches behavior in three buckets: PZEM, KWS single-phase,
//  KWS three-phase. Anything unrecognised falls back to PZEM-like.
// ────────────────────────────────────────────────────────────────
type SensorKind = 'pzem' | 'kws-1p' | 'kws-3p' | 'unknown';
function sensorKind(
  s: { model?: string | null; code?: string; phases?: 1 | 3 | null },
): SensorKind {
  // `phases` is the source of truth when the operator has set it in
  // the admin form. Falls back to a string-match heuristic on model/
  // code for legacy rows that pre-date the phases column.
  if (s.phases === 3) return 'kws-3p';
  if (s.phases === 1) return 'kws-1p';
  const m = (s.model ?? '').toUpperCase();
  const c = (s.code ?? '').toUpperCase();
  if (m.includes('PZEM') || c.startsWith('PZEM')) return 'pzem';
  if (m.includes('AC306') || m.includes('3P') || m.includes('THREE')) return 'kws-3p';
  if (m.startsWith('KWS') || c.startsWith('KWS')) return 'kws-1p';
  return 'unknown';
}
// Sensor-kind badge (label + color) — mirrors the Devices list page style.
const KIND_BADGE: Record<SensorKind, { label: string; color: string } | null> = {
  'pzem':   { label: 'PZEM',   color: '#06b6d4' },
  'kws-1p': { label: 'KWS-1P', color: '#a78bfa' },
  'kws-3p': { label: 'KWS-3P', color: '#facc15' },
  'unknown': null,
};

// Realtime chart windowing (tick step, span, round-boundary axis) lives in
// @/features/dashboard/compareChart so the pop-out window can share it.

// ────────────────────────────────────────────────────────────────
//  Page
// ────────────────────────────────────────────────────────────────
export function DashboardPage() {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const id = siteId ? Number(siteId) : null;

  const themeMode = useTheme();
  const ct = CHART_THEMES[themeMode];

  const site = useSiteRealtime(id ?? 0);
  const { data: sensors = [] } = useSiteSensors(id);
  const { data: zones = [] } = useZones(id);
  const { data: boards = [] } = useBoards(id ?? undefined);

  // Tick once per second so freshness badges and "X seconds ago" stay live.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // ─── Scope (filter cascade) ────────────────────────────────────
  // Sidebar already owns Site. We add: Zone → Board → SensorType → search.
  const [zoneId, setZoneId]     = useState<number | 'all'>('all');
  const [boardId, setBoardId]   = useState<number | 'all'>('all');
  const [kind, setKind]         = useState<SensorKind | 'all'>('all');
  const [search, setSearch]     = useState('');

  // Reset child selections when parent changes so we never sit on an
  // out-of-scope value.
  useEffect(() => { setBoardId('all'); }, [zoneId]);

  const activeSensors = useMemo(
    () => sensors.filter((s) => s.isActive),
    [sensors],
  );

  const boardsByZone = useMemo(() => {
    const m = new Map<number | 'none', BoardWithSensors[]>();
    for (const b of boards) {
      const z = (b.zoneId ?? null) as number | null;
      const k = z ?? 'none';
      const list = m.get(k) ?? [];
      list.push(b);
      m.set(k, list);
    }
    return m;
  }, [boards]);

  const boardsInScope = useMemo(() => {
    return boards.filter((b) =>
      (zoneId === 'all' || (b.zoneId ?? null) === zoneId) &&
      b.isActive,
    );
  }, [boards, zoneId]);

  const sensorsInScope = useMemo<SensorWithContext[]>(() => {
    return activeSensors.filter((s) => {
      if (zoneId !== 'all' && (s.zoneId ?? null) !== zoneId) return false;
      if (boardId !== 'all' && Number(s.boardId) !== boardId) return false;
      if (kind !== 'all' && sensorKind(s) !== kind) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${s.code} ${s.name ?? ''} ${s.boardCode ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [activeSensors, zoneId, boardId, kind, search]);

  // Counted meters only — the board's sensorAggMode decides this (primary-only
  // vs every sensor). Used for power aggregation so a board's PZEM+KWS on the
  // SAME circuit don't double-count, while independent-load boards count both.
  const counts = (s: SensorWithContext) => s.countInTotal ?? s.isPrimary;
  const countedInScopeIds = useMemo(
    () => new Set(sensorsInScope.filter(counts).map((s) => s.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sensorsInScope],
  );

  // ─── History backfill ────────────────────────────────────────────
  // The realtime store starts empty on every page load, so a wide chart window
  // (5 นาที+) would show nothing until enough live points trickle in. Seed each
  // counted sensor's history once from the backend (last 6 h) so the chart is
  // populated immediately; realtime updates then append on top.
  const seedSensorHistory = useRealtimeStore((s) => s.seedSensorHistory);
  const seededSitesRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (id == null || seededSitesRef.current.has(id)) return;
    const counted = activeSensors.filter((s) => s.countInTotal ?? s.isPrimary);
    if (counted.length === 0) return; // sensors not loaded yet
    seededSitesRef.current.add(id);
    const to = new Date().toISOString();
    const from = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
    (async () => {
      for (const s of counted) {
        try {
          const res = await api.get<Array<{
            time: string; voltage: number | null; current: number | null;
            power: number | null; energy: number | null; temperature: number | null; humidity: number | null;
          }>>('/api/telemetry/series', {
            params: { siteId: id, sensorId: s.id, range: 'custom', from, to },
          });
          const pts: TelemetryPoint[] = (res.data ?? [])
            .filter((p) => p.power != null || p.voltage != null || p.current != null)
            .map((p) => ({
              time: p.time, sensorId: s.id, siteId: id,
              voltage: p.voltage, current: p.current, power: p.power,
              energy: p.energy, temperature: p.temperature, humidity: p.humidity ?? null,
            }));
          if (pts.length) seedSensorHistory(id, s.id, pts);
        } catch {
          // ignore — realtime will still fill in over time
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeSensors]);

  // ─── Realtime aggregates from in-memory store ────────────────────
  // We compute over the in-scope sensors only. Each sensor's latest reading
  // is gated by freshness so a stale row never contributes to "live" metrics.
  const live = useMemo(() => {
    let powerSum = 0;
    let powerCount = 0;
    let voltageSum = 0;
    let voltageCount = 0;
    let currentSum = 0;
    let currentCount = 0;
    let tempMax: number | null = null;
    let voltageMin: number | null = null;
    let voltageMax: number | null = null;
    let freshSensors = 0;
    let totalSensors = 0;
    let mostRecentTime: number | null = null;

    for (const s of sensorsInScope) {
      totalSensors += 1;
      const state = site?.bySensor?.[s.id];
      const latest = state?.latest;
      if (!latest?.time) continue;
      const ts = new Date(latest.time).getTime();
      const f = freshness(ts, now);
      if (f === 'offline' || f === 'never') continue;
      if (f === 'fresh' || f === 'delayed') freshSensors += 1;
      if (mostRecentTime == null || ts > mostRecentTime) mostRecentTime = ts;

      // Electrical totals come from the board's COUNTED meters only (per its
      // sensorAggMode): primary-only for two meters on the same circuit, or
      // every sensor for meters on independent loads. Mirrors the backend.
      if (!counts(s)) continue;
      if (latest.power != null)   { powerSum   += latest.power;   powerCount += 1; }
      if (latest.voltage != null) {
        voltageSum += latest.voltage; voltageCount += 1;
        voltageMin = voltageMin == null ? latest.voltage : Math.min(voltageMin, latest.voltage);
        voltageMax = voltageMax == null ? latest.voltage : Math.max(voltageMax, latest.voltage);
      }
      if (latest.current != null) { currentSum += latest.current; currentCount += 1; }
      if (latest.temperature != null) {
        tempMax = tempMax == null ? latest.temperature : Math.max(tempMax, latest.temperature);
      }
    }

    return {
      powerSum,
      voltageAvg: voltageCount ? voltageSum / voltageCount : null,
      voltageMin, voltageMax,
      currentSum: currentCount ? currentSum : null,
      tempMax,
      freshSensors,
      totalSensors,
      mostRecentTime,
    };
  }, [sensorsInScope, site, now]);

  // Phase 3 data: live alerts + per-zone energy aggregates.
  const { data: alerts = [] }     = useAlerts(id, true);
  const { data: zoneSummary = [] } = useZoneSummary(id, 'today');
  const ackMut = useAckAlert(id);

  // ─── Realtime chart series ──────────────────────────────────────
  const [chartWindow, setChartWindow] = useState<ChartWindow>('15m');
  // Zone-vs-board comparison chart (one line per zone or per board) + which
  // metric to compare. Voltage is averaged across a group's meters; power &
  // current are summed (total load).
  const [compareBy, setCompareBy] = useState<'zone' | 'board'>('zone');
  const [compareMetric, setCompareMetric] = useState<'power' | 'voltage' | 'current'>('power');
  const chartData = useMemo(() => {
    const tickMs = CHART_TICK_MS[chartWindow];
    const win = tickMs * SPAN_STEPS;
    // ~10 buckets per tick interval → a smooth line within each gridline cell.
    const bucketMs = Math.max(2_000, Math.floor(tickMs / 10));
    const nowBucket = Math.floor(now / bucketMs) * bucketMs;
    const startBucket = nowBucket - Math.floor(win / bucketMs) * bucketMs;
    // bucket → sensorId → {sum,count}. We average PER SENSOR within a bucket,
    // then SUM those averages across the (primary) meters — giving the total
    // instantaneous power at that time. (Summing raw readings would inflate a
    // bucket that happened to catch 2+ readings from one sensor.)
    const byTime = new Map<number, Map<number, { sum: number; count: number }>>();
    if (site?.bySensor) {
      for (const [sidStr, state] of Object.entries(site.bySensor)) {
        const sid = Number(sidStr);
        if (!countedInScopeIds.has(sid)) continue;
        for (const p of state.history as TelemetryPoint[]) {
          const ts = new Date(p.time).getTime();
          if (ts < startBucket) continue;
          const bucket = Math.floor(ts / bucketMs) * bucketMs;
          const perSensor = byTime.get(bucket) ?? new Map<number, { sum: number; count: number }>();
          const e = perSensor.get(sid) ?? { sum: 0, count: 0 };
          e.sum += p.power ?? 0;
          e.count += 1;
          perSensor.set(sid, e);
          byTime.set(bucket, perSensor);
        }
      }
    }
    // Emit a CONTINUOUS bucket timeline across the whole selected window so the
    // x-axis always spans the chosen range — buckets with no data become null
    // (a gap in the line) instead of collapsing the axis to just the data.
    const out: { t: number; power: number | null }[] = [];
    for (let t = startBucket; t <= nowBucket; t += bucketMs) {
      const perSensor = byTime.get(t);
      if (!perSensor) { out.push({ t, power: null }); continue; }
      let power = 0;
      for (const e of perSensor.values()) power += e.count ? e.sum / e.count : 0;
      out.push({ t, power }); // Watts
    }
    return out;
  }, [site, countedInScopeIds, chartWindow, now]);
  const chartHasData = chartData.some((p) => p.power != null);

  // ─── Zone/board V·A·W comparison (multi-line, shared builder) ───
  // Reused by the pop-out window (@/features/dashboard/compareChart).
  const compareChart = useMemo(() => buildCompareOption({
    bySensor: site?.bySensor,
    activeSensors, countedIds: countedInScopeIds, zones, boards,
    compareBy, metric: compareMetric, chartWindow, now, ct,
  }), [site, activeSensors, countedInScopeIds, zones, boards, compareBy, compareMetric, chartWindow, now, ct]);

  const chartOption = useMemo(() => {
    // A TIME axis (not category) with a FIXED tick step per window so labels
    // land on round clock boundaries and never overlap on the narrow chart.
    const { tickMs, fmt: xFmt, min: xMin, max: xMax } =
      axisTicks(CHART_TICK_MS[chartWindow], now);
    return {
    grid: { left: 50, right: 16, top: 14, bottom: 32 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: ct.tooltipBg,
      borderColor: ct.tooltipBorder,
      textStyle: { color: ct.tooltipText },
      formatter: (params: Array<{ axisValue: number; value: [number, number | null] }>) => {
        const p = params[0];
        const label = dayjs(p.axisValue).format('HH:mm:ss');
        const v = p?.value?.[1];
        if (v == null) return `${label}<br/>ไม่มีข้อมูล`;
        return `${label}<br/>กำลังไฟ ${Number(v).toFixed(1)} W`;
      },
    },
    xAxis: {
      // VALUE axis (time treated as a numeric ms value) — unlike the time axis
      // it honours `interval` exactly, so ticks step by tickMs anchored to now.
      type: 'value',
      min: xMin,
      max: xMax,
      interval: tickMs,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: ct.label, fontSize: 10.5, hideOverlap: true, formatter: (val: number) => dayjs(val).format(xFmt) },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'W',
      nameGap: 10,
      nameTextStyle: { color: ct.label, fontSize: 10.5, align: 'left' },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: ct.label, fontSize: 10.5 },
      splitLine: { lineStyle: { color: ct.grid } },
    },
    series: [{
      type: 'line',
      smooth: true,
      symbol: 'none',
      connectNulls: true,
      lineStyle: { color: 'var(--cyan)', width: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(6, 182, 212, 0.3)' },
            { offset: 1, color: 'rgba(6, 182, 212, 0)' },
          ],
        },
      },
      data: chartData.map((p) => [p.t, p.power != null ? Number(p.power.toFixed(2)) : null]),
    }],
    };
  }, [chartData, ct, chartWindow, now]);

  // ─── Per-zone aggregations (for Zone Overview + Mini Comparison) ─
  type ZoneAgg = {
    id: number | null;
    code: string;
    name: string;
    boardOnline: number;
    boardTotal: number;
    sensorOnline: number;
    sensorTotal: number;
    powerNow: number;
    voltage: number | null;
    current: number | null;
    frequency: number | null;
    temperature: number | null;
    energyToday: number;
    worstFreshness: Freshness;
  };
  const zoneAggs = useMemo<ZoneAgg[]>(() => {
    const result: ZoneAgg[] = [];
    const list: Array<{ id: number | null; code: string; name: string }> = [
      ...zones.map((z) => ({ id: z.id as number | null, code: z.code, name: z.name })),
    ];
    const unzonedBoards = boardsByZone.get('none') ?? [];
    if (unzonedBoards.length > 0) {
      list.push({ id: null, code: '—', name: 'ไม่ระบุโซน' });
    }
    for (const z of list) {
      const zb = boards.filter((b) => (b.zoneId ?? null) === z.id);
      const zsensors = activeSensors.filter((s) => (s.zoneId ?? null) === z.id);
      let bOnline = 0, sOnline = 0, pNow = 0, eDay = 0;
      let vSum = 0, vN = 0, iSum = 0, fSum = 0, fN = 0, tMax: number | null = null;
      let anyBoardData = false;
      for (const b of zb) {
        const bs = activeSensors.filter((x) => Number(x.boardId) === b.id);
        const latestForBoard = bs
          .map((s) => site?.bySensor?.[s.id]?.latest?.time)
          .filter(Boolean)
          .map((t) => new Date(t!).getTime());
        const boardTs = latestForBoard.length ? Math.max(...latestForBoard) : null;
        if (boardTs != null) anyBoardData = true;
        // Standard board status (broker presence first, telemetry-freshness fallback).
        if (boardStatusOf(b, boardTs, now) === 'online') bOnline += 1;
      }
      // Zone status derived from the actual counts — NOT a default of 'fresh'
      // (a zone with no boards, or with every board offline, must not read ONLINE).
      const worst: Freshness =
        zb.length === 0 ? 'never'
        : bOnline === 0 ? (anyBoardData ? 'offline' : 'never')
        : bOnline < zb.length ? 'delayed'
        : 'fresh';
      for (const s of zsensors) {
        const state = site?.bySensor?.[s.id];
        const latest = state?.latest;
        if (!latest?.time) continue;
        const ts = new Date(latest.time).getTime();
        const f = freshness(ts, now);
        if (f === 'fresh' || f === 'delayed') {
          sOnline += 1;
          // Counted meters only (per board sensorAggMode) — avoid double-count.
          // Power/current summed, voltage/frequency averaged, temperature max.
          if (counts(s)) {
            if (latest.power != null) pNow += latest.power;
            if (latest.voltage != null) { vSum += latest.voltage; vN += 1; }
            if (latest.current != null) iSum += latest.current;
            const fr = latest.raw && typeof latest.raw.frequency === 'number' ? latest.raw.frequency : null;
            if (fr != null) { fSum += fr; fN += 1; }
          }
          if (latest.temperature != null) tMax = tMax == null ? latest.temperature : Math.max(tMax, latest.temperature);
        }
        // Approximate "energy today" from the in-memory history (first vs last).
        const h = state?.history ?? [];
        if (h.length > 1) {
          const a = h[0]?.energy ?? null;
          const b = h[h.length - 1]?.energy ?? null;
          if (a != null && b != null) eDay += Math.max(0, b - a);
        }
      }
      result.push({
        id: z.id, code: z.code, name: z.name,
        boardOnline: bOnline, boardTotal: zb.length,
        sensorOnline: sOnline, sensorTotal: zsensors.length,
        // Prefer the backend zone-summary row when available — it's the
        // accurate "today" number from a real SQL aggregate rather than
        // the in-memory history approximation (eDay).
        powerNow: pNow,
        voltage: vN ? vSum / vN : null,
        current: sOnline > 0 ? iSum : null,
        frequency: fN ? fSum / fN : null,
        temperature: tMax,
        energyToday:
          zoneSummary.find((zs: ZoneSummaryRow) => zs.zoneId === z.id)?.energy
          ?? eDay,
        worstFreshness: worst,
      });
    }
    return result;
  }, [zones, boards, boardsByZone, activeSensors, site, now, zoneSummary]);

  // ─── Analysis: today-vs-yesterday, top consumers, auto insights ──
  // Ranges recompute once a minute (not every 1s tick) so the summary queries
  // don't refetch in a storm. "Yesterday" is clipped to the SAME elapsed time
  // as today for an apples-to-apples comparison.
  const nowMin = Math.floor(now / 60_000);
  const cmpRanges = useMemo(() => {
    const startToday = dayjs().startOf('day');
    const elapsedMs = Date.now() - startToday.valueOf();
    const startYest = startToday.subtract(1, 'day');
    return {
      today: { from: startToday.toISOString(), to: dayjs().toISOString() },
      yest:  { from: startYest.toISOString(), to: startYest.add(elapsedMs, 'millisecond').toISOString() },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowMin]);
  const cmpScope = zoneId !== 'all' ? { zoneId: zoneId as number } : {};
  const { data: todaySum } = useTelemetrySummary(
    id != null ? { siteId: id, range: 'custom' as const, from: cmpRanges.today.from, to: cmpRanges.today.to, ...cmpScope } : null,
  );
  const { data: yestSum } = useTelemetrySummary(
    id != null ? { siteId: id, range: 'custom' as const, from: cmpRanges.yest.from, to: cmpRanges.yest.to, ...cmpScope } : null,
  );
  const energyTodayCal = todaySum?.energy?.delta ?? null;
  const energyYest = yestSum?.energy?.delta ?? null;

  // ─── Redesigned-dashboard data (all realtime from the store) ─────
  // Energy sparkline = running integral of power (∝ accumulated energy).
  const energySpark = useMemo(() => {
    let acc = 0;
    return chartData
      .map((p) => p.power)
      .filter((v): v is number => v != null)
      .slice(-40)
      .map((v) => (acc += Math.max(0, v)));
  }, [chartData]);

  // Voltage (avg of counted meters) + Current (sum) history over the chart
  // window — per-sensor averaged within a bucket, then combined across meters.
  const viHistory = useMemo(() => {
    const tickMs = CHART_TICK_MS[chartWindow];
    const win = tickMs * SPAN_STEPS;
    const bucketMs = Math.max(2_000, Math.floor(tickMs / 10));
    const nowBucket = Math.floor(now / bucketMs) * bucketMs;
    const startBucket = nowBucket - Math.floor(win / bucketMs) * bucketMs;
    const byT = new Map<number, Map<number, { v: number; vc: number; i: number; ic: number; tmax: number | null }>>();
    if (site?.bySensor) {
      for (const [sidStr, state] of Object.entries(site.bySensor)) {
        const sid = Number(sidStr);
        if (!countedInScopeIds.has(sid)) continue;
        for (const p of state.history as TelemetryPoint[]) {
          const ts = new Date(p.time).getTime(); if (ts < startBucket) continue;
          const b = Math.floor(ts / bucketMs) * bucketMs;
          const per = byT.get(b) ?? new Map();
          const e = per.get(sid) ?? { v: 0, vc: 0, i: 0, ic: 0, tmax: null };
          if (p.voltage != null) { e.v += p.voltage; e.vc++; }
          if (p.current != null) { e.i += p.current; e.ic++; }
          if (p.temperature != null) e.tmax = e.tmax == null ? p.temperature : Math.max(e.tmax, p.temperature);
          per.set(sid, e); byT.set(b, per);
        }
      }
    }
    // Continuous timeline across the whole window (null where no data) so the
    // x-axis matches the selected range, same as the power chart.
    const out: { t: number; v: number | null; i: number | null; temp: number | null }[] = [];
    for (let t = startBucket; t <= nowBucket; t += bucketMs) {
      const per = byT.get(t);
      if (!per) { out.push({ t, v: null, i: null, temp: null }); continue; }
      let vSum = 0, vN = 0, iSum = 0, tMax: number | null = null;
      for (const e of per.values()) {
        if (e.vc) { vSum += e.v / e.vc; vN++; }
        if (e.ic) iSum += e.i / e.ic;
        if (e.tmax != null) tMax = tMax == null ? e.tmax : Math.max(tMax, e.tmax);
      }
      out.push({ t, v: vN ? vSum / vN : null, i: iSum, temp: tMax });
    }
    return out;
  }, [site, countedInScopeIds, chartWindow, now]);
  const viHasData = viHistory.some((d) => d.v != null || d.i != null);

  // Per-metric min / max / avg + sparkline over the chart window — computed
  // from the SAME aggregated series as the current value, so the stats are
  // consistent (current summed across meters, voltage averaged, etc.).
  const kpiStats = useMemo(() => {
    const statsOf = (series: (number | null)[]) => {
      const vals = series.filter((v): v is number => v != null);
      if (vals.length === 0) return { min: null as number | null, max: null as number | null, avg: null as number | null, spark: [] as number[] };
      let min = vals[0], max = vals[0], sum = 0;
      for (const v of vals) { if (v < min) min = v; if (v > max) max = v; sum += v; }
      return { min, max, avg: sum / vals.length, spark: vals.slice(-40) };
    };
    return {
      volt: statsOf(viHistory.map((d) => d.v)),
      curr: statsOf(viHistory.map((d) => d.i)),
      power: statsOf(chartData.map((p) => p.power)),
      temp: statsOf(viHistory.map((d) => d.temp)),
    };
  }, [viHistory, chartData]);

  // Board status split: online / offline / no-data (never reported).
  const boardStatus = useMemo(() => {
    let online = 0, offline = 0, noData = 0;
    for (const b of boards) {
      const bs = activeSensors.filter((s) => Number(s.boardId) === b.id);
      const times = bs.map((s) => site?.bySensor?.[s.id]?.latest?.time).filter(Boolean).map((t) => new Date(t!).getTime());
      const hasData = times.length > 0 || b.lastSeenAt != null;
      const isOn = boardStatusOf(b, times.length ? Math.max(...times) : null, now) === 'online';
      if (isOn) online += 1; else if (hasData) offline += 1; else noData += 1;
    }
    return { online, offline, noData, total: boards.length };
  }, [boards, activeSensors, site, now]);

  // Per-board "today" energy (since midnight) — same source & range as the
  // KPI and the zone bars, so the board table agrees with them. Sums only the
  // board's COUNTED meters (per sensorAggMode) to avoid double-counting.
  const energyByBoard = useMemo(() => {
    const m = new Map<number, number>();
    const primaryByBoard = new Map<number, Set<number>>();
    for (const s of activeSensors) if (counts(s)) {
      const set = primaryByBoard.get(Number(s.boardId)) ?? new Set(); set.add(s.id); primaryByBoard.set(Number(s.boardId), set);
    }
    for (const bs of todaySum?.bySensor ?? []) {
      for (const [bid, ids] of primaryByBoard) if (ids.has(bs.sensorId)) m.set(bid, (m.get(bid) ?? 0) + (bs.energyDelta ?? 0));
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSensors, todaySum]);
  const boardRows = useMemo(() => boards.map((b) => {
    const bs = activeSensors.filter((s) => Number(s.boardId) === b.id);
    let power = 0, sOnline = 0, latest: number | null = null, temp: number | null = null;
    let vSum = 0, vN = 0, curr = 0, hasCurr = false;
    for (const s of bs) {
      const l = site?.bySensor?.[s.id]?.latest;
      if (!l?.time) continue;
      const ts = new Date(l.time).getTime();
      const f = freshness(ts, now);
      if (f === 'fresh' || f === 'delayed') {
        sOnline += 1;
        // Electrical totals from COUNTED meters only (voltage averaged, power
        // & current summed) — mirrors the live/chart aggregation.
        if (counts(s)) {
          if (l.power != null) power += l.power;
          if (l.voltage != null) { vSum += l.voltage; vN += 1; }
          if (l.current != null) { curr += l.current; hasCurr = true; }
        }
        // Hottest reading among the board's live sensors (KWS report temp).
        if (l.temperature != null) temp = temp == null ? l.temperature : Math.max(temp, l.temperature);
      }
      latest = latest == null ? ts : Math.max(latest, ts);
    }
    // Which sensors this board carries — kind badge + flag the primary (star).
    const sensors = bs.map((s) => {
      const lt = site?.bySensor?.[s.id]?.latest?.time;
      const f = lt ? freshness(new Date(lt).getTime(), now) : 'never';
      return { code: s.code, kind: sensorKind(s), primary: counts(s), online: f === 'fresh' || f === 'delayed' };
    });
    // Standard 4-state board status, rendered via the shared badge.
    const status = boardStatusOf(b, latest, now);
    const zone = b.zoneId != null ? zones.find((z) => z.id === b.zoneId) : null;
    return {
      id: b.id, code: b.code, zone: zone?.code ?? '—', status, sOnline, sTotal: bs.length, sensors,
      power, volt: vN ? vSum / vN : null, curr: hasCurr ? curr : null,
      energy: energyByBoard.get(b.id) ?? 0, temp, latest,
    };
  }).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
  [boards, activeSensors, site, now, zones, energyByBoard]);

  // ─── Render ────────────────────────────────────────────────────
  if (id == null) {
    return (
      <div style={{ padding: 40, color: 'var(--dim)' }}>
        เลือก site จาก sidebar ก่อน
      </div>
    );
  }

  // Header liveness reflects the AGE of the freshest reading across in-scope
  // sensors — NOT the fraction of sensors reporting. A site with some boards
  // offline is still "Online" as long as live data is flowing (MQTT up); the
  // "X/Y sensors online" KPI already conveys partial coverage. This keeps
  // "Delayed" meaning genuine data lag (10–30 s) rather than "a board is off".
  const overallFreshness: Freshness =
    live.totalSensors === 0        ? 'never'
    : live.mostRecentTime == null  ? 'offline'
    : freshness(live.mostRecentTime, now);

  // Header + KPI helper values.
  const hOffline = overallFreshness === 'offline' || overallFreshness === 'never';
  const hColor = overallFreshness === 'fresh' ? '#22c55e' : hOffline ? '#ef4444' : '#f59e0b';
  const hLabel = overallFreshness === 'fresh' ? 'ออนไลน์' : hOffline ? 'ออฟไลน์' : 'หน่วง';
  // "MQTT" indicator = do any boards actually have a live broker connection.
  const brokerUp = boardStatus.online > 0;
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 };
  const energyKwhToday = energyTodayCal;
  const energyDeltaKwh = energyTodayCal != null && energyYest != null ? energyTodayCal - energyYest : null;

  return (
    <div id="dashboard-export-root">
      {/* ── Topbar: site title · alert bell · clock · user menu ── */}
      <PageHeader title="แดชบอร์ด" />

      {/* ── Live status strip ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: hColor }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: hColor }} /> {hLabel}
          </span>
          {/* Board↔broker connectivity (NOT the browser↔server socket) — so it
              reads "หลุด" when the devices themselves are offline. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--dim)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: brokerUp ? '#22c55e' : '#ef4444' }} /> MQTT {brokerUp ? `เชื่อมต่อ (${boardStatus.online}/${boardStatus.total})` : 'หลุด'}
          </span>
          <span style={{ color: 'var(--dim)' }}>อัปเดตล่าสุด {live.mostRecentTime ? dayjs(live.mostRecentTime).format('HH:mm:ss') : '—'}</span>
          {live.tempMax != null && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: live.tempMax >= 45 ? '#ef4444' : live.tempMax >= 40 ? '#f59e0b' : 'var(--dim)', fontWeight: live.tempMax >= 40 ? 700 : 400 }}>
              <Thermometer size={13} /> อุณหภูมิ {live.tempMax.toFixed(1)}°C
            </span>
          )}
        </div>
        {/* LIVE badge only while data is actually flowing (fresh/delayed), and
            tinted to match the freshness — so it never says green-LIVE next to
            a "Delayed" state. When offline the left chip carries the status. */}
        {(overallFreshness === 'fresh' || overallFreshness === 'delayed') && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700,
            color: hColor, padding: '6px 12px', borderRadius: 999,
            background: `${hColor}1a`, border: `1px solid ${hColor}59`, flexShrink: 0,
          }}>
            <span className="pulse-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: hColor, boxShadow: `0 0 0 4px ${hColor}2e` }} /> LIVE
          </span>
        )}
      </div>

      {/* Scope filter — narrow to a zone / board / sensor kind */}
      <ScopeFilter
        zones={zones} boards={boardsInScope}
        zoneId={zoneId} setZoneId={setZoneId}
        boardId={boardId} setBoardId={setBoardId}
        kind={kind} setKind={setKind}
        search={search} setSearch={setSearch}
      />

      {/* ── 5 KPI cards (realtime: value + min/avg/max + sparkline) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))', gap: 14, marginBottom: 16 }}>
        <KpiCard
          icon={<Zap size={16} />} accent="#06b6d4" label="แรงดัน"
          value={live.voltageAvg != null ? live.voltageAvg.toFixed(1) : '—'} unit="V"
          spark={kpiStats.volt.spark}
          stats={{ ...kpiStats.volt, fmt: (n) => n.toFixed(1) }}
        />
        <KpiCard
          icon={<Activity size={16} />} accent="#22c55e" label="กระแส"
          value={live.currentSum != null ? live.currentSum.toFixed(2) : '—'} unit="A"
          spark={kpiStats.curr.spark}
          stats={{ ...kpiStats.curr, fmt: (n) => n.toFixed(2) }}
        />
        <KpiCard
          icon={<Gauge size={16} />} accent="#f59e0b" label="กำลังไฟ"
          value={live.powerSum >= 1000 ? (live.powerSum / 1000).toFixed(2) : live.powerSum.toFixed(1)}
          unit={live.powerSum >= 1000 ? 'kW' : 'W'}
          spark={kpiStats.power.spark}
          stats={{ ...kpiStats.power, fmt: (n) => (n >= 1000 ? (n / 1000).toFixed(2) : n.toFixed(0)) }}
        />
        <KpiCard
          icon={<Thermometer size={16} />}
          accent={live.tempMax != null && live.tempMax >= 45 ? '#ef4444' : '#f43f5e'}
          label="อุณหภูมิ"
          value={live.tempMax != null ? live.tempMax.toFixed(1) : '—'} unit="°C"
          spark={kpiStats.temp.spark}
          stats={{ ...kpiStats.temp, fmt: (n) => n.toFixed(1) }}
        />
        <KpiCard
          icon={<BatteryCharging size={16} />} accent="#a78bfa" label="พลังงานวันนี้"
          value={energyKwhToday != null ? energyKwhToday.toFixed(3) : '—'} unit="kWh"
          sub={energyDeltaKwh != null ? `${energyDeltaKwh >= 0 ? '↑' : '↓'} ${Math.abs(energyDeltaKwh).toFixed(3)} kWh จากเมื่อวาน` : undefined}
          subColor={energyDeltaKwh == null ? undefined : energyDeltaKwh > 0 ? '#ef4444' : '#22c55e'}
          spark={energySpark}
        />
      </div>

      {/* ── Voltage/Current chart + latest alerts ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 16, marginBottom: 16 }}>
        <RealtimeChartCard
          title="แรงดัน / กระแส" window={chartWindow} setWindow={setChartWindow}
          isEmpty={!viHasData} scopeLabel={scopeLabel(zoneId, boardId, zones, boards)}
        >
          <VoltCurrentChart data={viHistory} tickMs={CHART_TICK_MS[chartWindow]} now={now} height="100%" />
        </RealtimeChartCard>
        <AlertPanel
          alerts={alerts}
          offlineBoards={boardRows.filter((r) => r.status !== 'online').map((r) => ({ id: r.id, code: r.code, status: r.status, latest: r.latest }))}
          onAcknowledge={(alertId) => ackMut.mutate(alertId)}
          navigate={navigate}
        />
      </div>

      {/* ── Zone energy bars · Power · Board status donut ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16, marginBottom: 16 }}>
        <DashCard title="พลังงานรายโซน (kWh)"><ZoneEnergyBars rows={zoneAggs} /></DashCard>
        <DashCard title="กำลังไฟ (W)">
          {chartHasData
            ? <ReactECharts option={chartOption} style={{ height: 210 }} lazyUpdate />
            : <div style={{ height: 210, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center', color: 'var(--dim)', fontSize: 12, textAlign: 'center' }}>
                <span>ไม่มีข้อมูลในช่วงที่เลือก</span>
                <span style={{ fontSize: 11, color: 'var(--dim2)' }}>ลองเลือกช่วงเวลากว้างขึ้น</span>
              </div>}
        </DashCard>
        <DashCard title="สัดส่วนสถานะบอร์ด" right={<button onClick={() => navigate('/admin/devices')} style={linkBtn}>ดูรายละเอียด →</button>}>
          <BoardDonut online={boardStatus.online} offline={boardStatus.offline} noData={boardStatus.noData} total={boardStatus.total} />
        </DashCard>
      </div>

      {/* ── Zone/board V·A·W comparison over time ── */}
      <div style={{ marginBottom: 16 }}>
      <DashCard
        title="เปรียบเทียบตามเวลา"
        right={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* metric: V (avg) · A · W */}
            <div style={{ display: 'flex', gap: 4 }}>
              {([['voltage', 'V'], ['current', 'A'], ['power', 'W']] as const).map(([m, lbl]) => {
                const active = m === compareMetric;
                return (
                  <button
                    key={m}
                    onClick={() => setCompareMetric(m)}
                    style={{
                      background: active ? 'var(--cyan-glow-strong)' : 'var(--bg-input)',
                      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border-color)'}`,
                      color: active ? 'var(--cyan)' : 'var(--text)',
                      padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                      fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: 'inherit', minWidth: 34,
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
            <span aria-hidden style={{ width: 1, height: 20, background: 'var(--border-color)' }} />
            {/* group by: zone · board */}
            <div style={{ display: 'flex', gap: 4 }}>
              {(['zone', 'board'] as const).map((m) => {
                const active = m === compareBy;
                return (
                  <button
                    key={m}
                    onClick={() => setCompareBy(m)}
                    style={{
                      background: active ? 'var(--cyan-glow-strong)' : 'var(--bg-input)',
                      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border-color)'}`,
                      color: active ? 'var(--cyan)' : 'var(--text)',
                      padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                      fontSize: 11.5, fontWeight: active ? 700 : 500, fontFamily: 'inherit',
                    }}
                  >
                    {m === 'zone' ? 'ตามโซน' : 'ตามบอร์ด'}
                  </button>
                );
              })}
            </div>
            <span aria-hidden style={{ width: 1, height: 20, background: 'var(--border-color)' }} />
            {/* Pop out to a separate window that shows V + A + W at once */}
            <button
              onClick={() => id != null && window.open(`/sites/${id}/compare`, `compare-${id}`, 'width=1180,height=820')}
              title="เปิดแยกหน้าต่าง (ดู V·A·W พร้อมกัน)"
              style={{
                background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text)',
                padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5,
              }}
            >
              <ExternalLink size={13} /> แยกหน้าต่าง
            </button>
          </div>
        }
      >
        {compareChart.hasData ? (
          // key changes on toggle → clean remount (series count / y-axis unit);
          // lazyUpdate (no notMerge) keeps per-second updates flicker-free.
          <ReactECharts key={`${compareBy}-${compareMetric}`} option={compareChart.option} style={{ height: 300 }} lazyUpdate />
        ) : (
          <div style={{ height: 300, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', justifyContent: 'center', color: 'var(--dim)', fontSize: 12, textAlign: 'center' }}>
            <span>ไม่มีข้อมูลในช่วงที่เลือก</span>
            <span style={{ fontSize: 11, color: 'var(--dim2)' }}>ลองเลือกช่วงเวลากว้างขึ้น หรือรอข้อมูลเรียลไทม์</span>
          </div>
        )}
      </DashCard>
      </div>

      {/* Zone overview cards */}
      {zoneAggs.length > 0 && (
        <ZoneOverview aggs={zoneAggs} onJump={(zid) => setZoneId(zid ?? 'all')} activeZoneId={zoneId} />
      )}

      {/* Board status table */}
      <DashCard
        title="สถานะอุปกรณ์"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '2px 10px', color: 'var(--dim)', fontSize: 11.5 }}>
              <HardDrive size={12} color="#a78bfa" /> {boardStatus.online}/{boardStatus.total} อุปกรณ์
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '2px 10px', color: 'var(--dim)', fontSize: 11.5 }}>
              <Cpu size={12} color="#38bdf8" /> {live.freshSensors}/{live.totalSensors} เซ็นเซอร์
            </span>
            <button onClick={() => navigate('/admin/devices')} style={linkBtn}>ดูทั้งหมด →</button>
          </div>
        }
      >
        <BoardTable rows={boardRows} onJump={(bid) => navigate(`/admin/devices/${bid}?tab=energy`)} />
      </DashCard>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Sub-components
// ────────────────────────────────────────────────────────────────

function scopeLabel(
  zoneId: number | 'all',
  boardId: number | 'all',
  zones: Array<{ id: number; code: string }>,
  boards: BoardWithSensors[],
): string {
  if (boardId !== 'all') {
    const b = boards.find((x) => x.id === boardId);
    return b ? `Board ${b.code}` : 'Board ที่เลือก';
  }
  if (zoneId !== 'all') {
    const z = zones.find((x) => x.id === zoneId);
    return z ? `Zone ${z.code}` : 'Zone ที่เลือก';
  }
  return 'ทั้ง Site';
}

function StatusDot({ state }: { state: Freshness }) {
  return (
    <span
      className={state === 'fresh' ? 'pulse-dot' : ''}
      style={{
        width: 8, height: 8, borderRadius: '50%',
        background: freshnessColor(state),
        display: 'inline-block',
        boxShadow: state === 'fresh'
          ? '0 0 0 4px rgba(34, 197, 94, 0.18)'
          : 'none',
      }}
    />
  );
}

function Pill({
  state, label,
}: { state: Freshness; label: string }) {
  const c = freshnessColor(state);
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 8px', borderRadius: 999,
        background: state === 'fresh' ? 'rgba(34, 197, 94, 0.1)'
                  : state === 'delayed' || state === 'stale' ? 'rgba(250, 204, 21, 0.1)'
                  : state === 'never' ? 'rgba(100, 116, 139, 0.12)'
                  : 'rgba(239, 68, 68, 0.1)',
        border: `1px solid ${c}55`,
        color: c, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.04,
      }}
    >
      <StatusDot state={state} />
      {label}
    </span>
  );
}

// ─── 1. System Status Bar ────────────────────────────────────────
function ScopeFilter(props: {
  zones: Array<{ id: number; code: string; name: string }>;
  boards: BoardWithSensors[];
  zoneId: number | 'all'; setZoneId: (v: number | 'all') => void;
  boardId: number | 'all'; setBoardId: (v: number | 'all') => void;
  kind: SensorKind | 'all'; setKind: (v: SensorKind | 'all') => void;
  search: string; setSearch: (v: string) => void;
}) {
  const { zones, boards, zoneId, setZoneId, boardId, setBoardId,
          kind, setKind, search, setSearch } = props;
  const [open, setOpen] = useState(false);

  // Current-value summary (shown while collapsed). A dimension left at its
  // default ("all"/empty) is dimmed; an active filter is highlighted.
  const kindLabel = kind === 'all' ? 'ทุกชนิด'
    : kind === 'pzem' ? 'PZEM'
    : kind === 'kws-1p' ? 'KWS 1-phase'
    : kind === 'kws-3p' ? 'KWS 3-phase' : String(kind);
  const zoneLabel = zoneId === 'all' ? 'ทุก Zone' : zones.find((z) => z.id === zoneId)?.code ?? 'Zone';
  const boardLabel = boardId === 'all' ? 'ทุก Board' : boards.find((b) => b.id === boardId)?.code ?? 'Board';
  const chips: Array<{ k: string; v: string; active: boolean }> = [
    { k: 'Zone', v: zoneLabel, active: zoneId !== 'all' },
    { k: 'Board', v: boardLabel, active: boardId !== 'all' },
    { k: 'ชนิด', v: kindLabel, active: kind !== 'all' },
    { k: 'ค้นหา', v: search || '—', active: search.trim() !== '' },
  ];
  const activeCount = chips.filter((c) => c.active).length;

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: 12,
        padding: '12px 16px',
        marginBottom: 14,
      }}
    >
      {/* Header row — always visible; toggles the fields below */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', padding: 0, width: '100%',
          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
          fontFamily: 'inherit', color: 'var(--text)', flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--dim)' }}>
          <SlidersHorizontal size={14} /> ตัวกรอง
          {activeCount > 0 && (
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--cyan)', background: 'rgba(6,182,212,0.12)', borderRadius: 999, padding: '1px 7px' }}>{activeCount}</span>
          )}
        </span>
        {/* Current filter values */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {chips.map((c) => (
            <span key={c.k} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5,
              padding: '2px 9px', borderRadius: 999,
              background: c.active ? 'rgba(6,182,212,0.10)' : 'var(--bg-input)',
              border: `1px solid ${c.active ? 'rgba(6,182,212,0.35)' : 'var(--border-color)'}`,
              color: c.active ? 'var(--cyan)' : 'var(--dim)',
              maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              <span style={{ opacity: 0.7 }}>{c.k}:</span>
              <span style={{ fontWeight: 600 }}>{c.v}</span>
            </span>
          ))}
        </span>
        <ChevronDown size={16} color="var(--dim)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end',
          marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-color)',
        }}>
          <Field label="Zone">
            <Select value={String(zoneId)} onChange={(v) => setZoneId(v === 'all' ? 'all' : Number(v))}>
              <option value="all">ทุก Zone</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>{z.code} · {z.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Board">
            <Select value={String(boardId)} onChange={(v) => setBoardId(v === 'all' ? 'all' : Number(v))}>
              <option value="all">ทุก Board{zoneId !== 'all' ? ' (ใน Zone)' : ''}</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>{b.code}{b.name ? ` · ${b.name}` : ''}</option>
              ))}
            </Select>
          </Field>
          <Field label="Sensor type">
            <Select value={kind} onChange={(v) => setKind(v as SensorKind | 'all')}>
              <option value="all">ทุกชนิด</option>
              <option value="pzem">PZEM</option>
              <option value="kws-1p">KWS 1-phase</option>
              <option value="kws-3p">KWS 3-phase</option>
            </Select>
          </Field>
          <Field label="ค้นหา">
            <div style={{ position: 'relative' }}>
              <Search size={14} color="var(--dim)" style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Board / Sensor code"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text)',
                  padding: '7px 10px 7px 30px',
                  borderRadius: 8, fontSize: 13,
                  fontFamily: 'inherit', width: 200, outline: 'none',
                }}
              />
            </div>
          </Field>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: 10.5,
        color: 'var(--dim)', fontWeight: 600,
        marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function Select(props: {
  value: string; onChange: (v: string) => void; children: React.ReactNode;
}) {
  return (
    <select
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-color)',
        color: 'var(--text)',
        padding: '7px 10px', borderRadius: 8, fontSize: 13,
        fontFamily: 'inherit', cursor: 'pointer', minWidth: 160,
      }}
    >
      {props.children}
    </select>
  );
}

function Sparkline({ data, color, height = 34, fmt, unit }: { data: number[]; color: string; height?: number; fmt?: (n: number) => string; unit?: string }) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length < 2) return <div style={{ height }} />;
  const w = 120;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const px = (i: number) => (i / (data.length - 1)) * w;
  const pts = data.map((v, i) => `${px(i).toFixed(1)},${(height - 3 - ((v - min) / span) * (height - 6)).toFixed(1)}`);
  const uid = `sg-${color.replace('#', '')}-${height}`;
  const hoverPct = hover != null ? (hover / (data.length - 1)) * 100 : 0;
  return (
    <div
      style={{ position: 'relative', height }}
      onMouseMove={fmt ? (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const idx = Math.round(((e.clientX - r.left) / r.width) * (data.length - 1));
        setHover(Math.max(0, Math.min(data.length - 1, idx)));
      } : undefined}
      onMouseLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
        <defs>
          <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`0,${height} ${pts.join(' ')} ${w},${height}`} fill={`url(#${uid})`} />
        <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
        {hover != null && fmt && (
          <line x1={px(hover)} y1={0} x2={px(hover)} y2={height} stroke={color} strokeWidth="1" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" opacity={0.7} />
        )}
      </svg>
      {hover != null && fmt && (
        <div style={{
          position: 'absolute', top: -20, left: `${hoverPct}%`, transform: `translateX(${hoverPct > 70 ? '-100%' : hoverPct < 30 ? '0' : '-50%'})`,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-color)', borderRadius: 5,
          padding: '2px 6px', fontSize: 10.5, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 5,
        }}>
          {fmt(data[hover])}{unit ? ` ${unit}` : ''}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, accent, label, value, unit, sub, subColor, spark, fraction, stats }: {
  icon: React.ReactNode; accent: string; label: string; value: string; unit?: string;
  sub?: React.ReactNode; subColor?: string; spark?: number[]; fraction?: { online: number; total: number };
  stats?: { min: number | null; max: number | null; avg: number | null; fmt: (n: number) => string };
}) {
  // Min–max range bar with the average marked as a dot on the track.
  const rangeBar = stats && (() => {
    const { min, max, avg, fmt } = stats;
    const has = min != null && max != null && avg != null;
    const avgPct = has && max > min ? Math.max(0, Math.min(100, ((avg - min) / (max - min)) * 100)) : 50;
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--dim2)', fontVariantNumeric: 'tabular-nums' }}>
          <span>ต่ำ {has ? fmt(min) : '—'}</span>
          <span>สูง {has ? fmt(max) : '—'}</span>
        </div>
        <div style={{ position: 'relative', height: 6, margin: '5px 0 4px', borderRadius: 3, background: 'var(--bg-input)' }}>
          {has && (
            <>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${avgPct}%`, background: `${accent}55`, borderRadius: 3 }} />
              <div style={{ position: 'absolute', left: `${avgPct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 9, height: 9, borderRadius: '50%', background: accent, border: '2px solid var(--bg-card)' }} />
            </>
          )}
        </div>
        <div style={{ textAlign: 'center', fontSize: 10.5, color: 'var(--text)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          เฉลี่ย {has ? fmt(avg) : '—'}
        </div>
      </div>
    );
  })();
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', minHeight: 118 }}>
      {/* Header: icon + label on the left, the big value on the top-right. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--dim)', fontWeight: 600, minWidth: 0 }}>
          <span style={{ display: 'inline-flex', width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center', background: `${accent}22`, color: accent, flexShrink: 0 }}>{icon}</span>
          {label}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.05, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {value}{unit && <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--dim)', marginLeft: 4 }}>{unit}</span>}
        </div>
      </div>
      {sub != null && <div style={{ marginTop: 6, fontSize: 11.5, color: subColor ?? 'var(--dim2)', fontWeight: subColor ? 700 : 400, textAlign: 'right' }}>{sub}</div>}
      {rangeBar}
      <div style={{ marginTop: 'auto', paddingTop: 10 }}>
        {spark && spark.length > 1
          ? <Sparkline data={spark} color={accent} fmt={stats?.fmt} unit={unit} />
          : fraction
            ? <div style={{ height: 7, borderRadius: 4, background: 'var(--bg-input)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${fraction.total ? (fraction.online / fraction.total) * 100 : 0}%`, background: accent, borderRadius: 4 }} />
              </div>
            : <div style={{ height: 34 }} />}
      </div>
    </div>
  );
}

function DashCard({ title, right, children, pad = 18 }: { title: string; right?: React.ReactNode; children: React.ReactNode; pad?: number }) {
  return (
    <section className="card" style={{ padding: pad, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

function ZoneEnergyBars({ rows }: { rows: { code: string; name: string; energyToday: number }[] }) {
  const sorted = [...rows].sort((a, b) => b.energyToday - a.energyToday);
  if (sorted.length === 0) return <div style={{ color: 'var(--dim)', fontSize: 12, padding: 12 }}>ไม่มีโซน</div>;
  const opt = {
    grid: { left: 4, right: 44, top: 4, bottom: 4, containLabel: true },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(17,23,41,0.95)', borderColor: 'rgba(148,163,184,0.2)', textStyle: { color: '#f1f5f9' }, valueFormatter: (v: number) => `${Number(v).toFixed(3)} kWh` },
    xAxis: { type: 'value', axisLabel: { color: '#94a3b8', fontSize: 10 }, splitLine: { lineStyle: { color: 'rgba(148,163,184,0.12)' } } },
    yAxis: { type: 'category', inverse: true, data: sorted.map((z) => z.code), axisLabel: { color: '#cbd5e1', fontSize: 11 }, axisLine: { show: false }, axisTick: { show: false } },
    series: [{ type: 'bar', data: sorted.map((z) => z.energyToday), barWidth: 13, itemStyle: { color: '#06b6d4', borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', color: '#cbd5e1', fontSize: 10.5, formatter: (p: { value: number }) => p.value.toFixed(3) } }],
  };
  return <ReactECharts option={opt} style={{ height: Math.max(130, sorted.length * 38) }} notMerge lazyUpdate />;
}

function VoltCurrentChart({ data, tickMs: tickStep, now, height = 210 }: { data: { t: number; v: number | null; i: number | null }[]; tickMs: number; now: number; height?: number | string }) {
  // TIME axis with a FIXED tick step, anchored to `now` (matches the power
  // chart) → ticks step back from the current time, no overlap.
  const { tickMs, fmt: xFmt, min, max } = axisTicks(tickStep, now);
  const opt = {
    grid: { left: 42, right: 42, top: 26, bottom: 24 },
    legend: { top: 0, textStyle: { color: '#cbd5e1', fontSize: 11 }, icon: 'roundRect', itemWidth: 14, itemHeight: 8 },
    tooltip: {
      trigger: 'axis', backgroundColor: 'rgba(17,23,41,0.95)', borderColor: 'rgba(148,163,184,0.2)', textStyle: { color: '#f1f5f9', fontSize: 12 },
      // Value axis → axisValue is a raw ms timestamp; format it as time and
      // append the right unit per series (V / A) instead of showing the number.
      formatter: (params: Array<{ axisValue: number; seriesName: string; marker: string; value: [number, number | null] }>) => {
        const head = dayjs(params[0]?.axisValue).format('HH:mm:ss');
        const lines = params.map((p) => {
          const isV = p.seriesName.includes('แรงดัน');
          const v = p.value?.[1];
          return `${p.marker} ${p.seriesName}: ${v != null ? Number(v).toFixed(isV ? 1 : 3) : '—'}`;
        });
        return [head, ...lines].join('<br/>');
      },
    },
    xAxis: { type: 'value', min, max, interval: tickMs, axisLabel: { color: '#94a3b8', fontSize: 10, hideOverlap: true, formatter: (val: number) => dayjs(val).format(xFmt) }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
    yAxis: [
      { type: 'value', name: 'V', min: 0, axisLabel: { color: '#94a3b8', fontSize: 10 }, nameTextStyle: { color: '#cbd5e1', fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: 'rgba(148,163,184,0.12)' } } },
      { type: 'value', name: 'A', min: 0, axisLabel: { color: '#94a3b8', fontSize: 10 }, nameTextStyle: { color: '#cbd5e1', fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
    ],
    series: [
      { name: 'แรงดัน (V)', type: 'line', smooth: true, symbol: 'none', connectNulls: true, yAxisIndex: 0, data: data.map((d) => [d.t, d.v]), lineStyle: { color: '#06b6d4', width: 2 }, itemStyle: { color: '#06b6d4' } },
      { name: 'กระแส (A)', type: 'line', smooth: true, symbol: 'none', connectNulls: true, yAxisIndex: 1, data: data.map((d) => [d.t, d.i]), lineStyle: { color: '#22c55e', width: 2 }, itemStyle: { color: '#22c55e' } },
    ],
  };
  return <ReactECharts option={opt} style={{ height }} lazyUpdate />;
}

function BoardDonut({ online, offline, noData, total }: { online: number; offline: number; noData: number; total: number }) {
  const opt = {
    tooltip: { trigger: 'item', backgroundColor: 'rgba(17,23,41,0.95)', borderColor: 'rgba(148,163,184,0.2)', textStyle: { color: '#f1f5f9' } },
    series: [{
      type: 'pie', radius: ['62%', '86%'], center: ['50%', '50%'], avoidLabelOverlap: false,
      label: { show: false }, labelLine: { show: false },
      data: [
        { value: online, name: 'ออนไลน์', itemStyle: { color: '#22c55e' } },
        { value: offline, name: 'ออฟไลน์', itemStyle: { color: '#ef4444' } },
        { value: noData, name: 'ไม่มีข้อมูล', itemStyle: { color: '#64748b' } },
      ].filter((d) => d.value > 0),
    }],
  };
  const legend: Array<[string, string, number]> = [['ออนไลน์', '#22c55e', online], ['ออฟไลน์', '#ef4444', offline], ['ไม่มีข้อมูล', '#64748b', noData]];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
        <ReactECharts option={opt} style={{ height: 150, width: 150 }} notMerge lazyUpdate />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{total}</div>
          <div style={{ fontSize: 11, color: 'var(--dim)' }}>บอร์ด</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 100 }}>
        {legend.map(([lbl, c, val]) => (
          <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />
            <span style={{ color: 'var(--dim)' }}>{lbl}</span>
            <span style={{ marginLeft: 'auto', color: 'var(--text)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {val} <span style={{ color: 'var(--dim2)', fontWeight: 500 }}>({total ? Math.round((val / total) * 100) : 0}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type BoardRow = { id: number; code: string; zone: string; status: BoardStatus; sOnline: number; sTotal: number; sensors: { code: string; kind: SensorKind; primary: boolean; online: boolean }[]; power: number; volt: number | null; curr: number | null; energy: number; temp: number | null; latest: number | null };
function BoardTable({ rows, onJump }: { rows: BoardRow[]; onJump: (id: number) => void }) {
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, color: 'var(--dim2)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0 12px 10px', fontWeight: 600, whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '11px 12px', fontSize: 12.5, color: 'var(--text)', whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>บอร์ด</th><th style={th}>โซน</th><th style={th}>สถานะ</th>
          <th style={th}>เซ็นเซอร์</th>
          <th style={{ ...th, textAlign: 'right' }}>แรงดัน (V)</th>
          <th style={{ ...th, textAlign: 'right' }}>กระแส (A)</th>
          <th style={{ ...th, textAlign: 'right' }}>กำลังไฟ (W)</th>
          <th style={{ ...th, textAlign: 'right' }}>พลังงานวันนี้ (kWh)</th>
          <th style={{ ...th, textAlign: 'right' }}>อุณหภูมิ (°C)</th><th style={th}>อัปเดตล่าสุด</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)', cursor: 'pointer' }} onClick={() => onJump(r.id)}>
              <td style={{ ...td, fontWeight: 700 }}>{r.code}</td>
              <td style={{ ...td, color: 'var(--dim)' }}>{r.zone}</td>
              <td style={td}><BoardStatusBadge status={r.status} /></td>
              <td style={td}>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {r.sensors.length === 0
                    ? <span style={{ fontSize: 11, color: 'var(--dim2)', fontStyle: 'italic' }}>ยังไม่มี</span>
                    : r.sensors.map((s) => {
                        const badge = KIND_BADGE[s.kind];
                        const color = badge?.color ?? '#94a3b8';
                        return (
                          <span key={s.code} title={`${s.code}${s.primary ? ' · เซ็นเซอร์หลัก' : ''}${s.online ? '' : ' · ออฟไลน์'}`} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 6,
                            background: `${color}1f`, color,
                            border: `1px solid ${color}40`,
                            fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em',
                            opacity: s.online ? 1 : 0.45,
                          }}>
                            {s.primary && <Star size={10} fill="currentColor" strokeWidth={0} />}
                            {badge?.label ?? s.code}
                          </span>
                        );
                      })}
                </span>
              </td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.status === 'online' && r.volt != null ? r.volt.toFixed(1) : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.status === 'online' && r.curr != null ? r.curr.toFixed(2) : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.status === 'online' ? r.power.toFixed(1) : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.energy.toFixed(3)}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.temp != null && r.temp >= 45 ? '#ef4444' : r.temp != null && r.temp >= 40 ? '#f59e0b' : 'var(--text)' }}>{r.status === 'online' && r.temp != null ? `${r.temp.toFixed(1)}°` : '—'}</td>
              <td style={{ ...td, color: 'var(--dim)' }}>{r.latest ? dayjs(r.latest).format('HH:mm:ss') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RealtimeChartCard(props: {
  title: string;
  window: ChartWindow;
  setWindow: (w: ChartWindow) => void;
  isEmpty: boolean;
  scopeLabel: string;
  children: React.ReactNode;
}) {
  const windows: Array<{ key: ChartWindow; label: string }> = [
    { key: '30s', label: '30 วิ' },
    { key: '1m', label: '1 นาที' },
    { key: '5m', label: '5 นาที' },
    { key: '15m', label: '15 นาที' },
    { key: '30m', label: '30 นาที' },
    { key: '1h', label: '1 ชั่วโมง' },
  ];
  return (
    <section className="card" style={{ padding: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10, marginBottom: 12,
      }}>
        <div>
          <span className="card-title">{props.title}</span>
          <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 2 }}>
            {props.scopeLabel} · อัปเดตทุก ~3 วินาที
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {windows.map((w) => {
            const active = w.key === props.window;
            return (
              <button
                key={w.key}
                onClick={() => props.setWindow(w.key)}
                style={{
                  background: active ? 'var(--cyan)' : 'var(--bg-input)',
                  border: `1px solid ${active ? 'var(--cyan)' : 'var(--border-color)'}`,
                  color: active ? '#000' : 'var(--text)',
                  padding: '5px 10px', borderRadius: 6,
                  cursor: 'pointer', fontSize: 11.5,
                  fontWeight: active ? 700 : 500, fontFamily: 'inherit',
                }}
              >
                {w.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ height: 280 }}>
        {props.isEmpty ? (
          <div style={{
            height: '100%', display: 'flex', flexDirection: 'column', gap: 5,
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--dim)', fontSize: 13, textAlign: 'center',
          }}>
            <span>ไม่มีข้อมูลในช่วงที่เลือก</span>
            <span style={{ fontSize: 11.5, color: 'var(--dim2)' }}>ลองเลือกช่วงเวลากว้างขึ้น หรือรอข้อมูลใหม่</span>
          </div>
        ) : (
          props.children
        )}
      </div>
    </section>
  );
}

// ─── 4b. Today Usage ───────────────────────────────────────────
function AlertPanel(props: {
  alerts: AlertRow[];
  offlineBoards: { id: number; code: string; status: BoardStatus; latest: number | null }[];
  onAcknowledge: (alertId: number) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { onAcknowledge, navigate } = props;
  // Real alerts: X acks them → filter out anything acknowledged so it stays
  // gone after a refresh (acknowledgedAt is persisted by the backend). The
  // in-memory `ackedNow` set just hides them instantly before the refetch.
  const [ackedNow, setAckedNow] = useState<Set<number>>(new Set());
  // Offline-board items are client-derived (no backend row) — persist their
  // dismissal in localStorage, keyed per offline EPISODE (board id + last-seen)
  // so a fresh offline event later still shows up.
  const STORE_KEY = 'dashboard-dismissed-boards';
  const [boardHidden, setBoardHidden] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORE_KEY) || '[]') as string[]); } catch { return new Set(); }
  });
  const boardKey = (b: { id: number; latest: number | null }) => `${b.id}:${b.latest ?? 'never'}`;
  const hideBoard = (key: string) => setBoardHidden((s) => {
    const n = new Set(s).add(key);
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });
  const [sortDir, setSortDir] = useState<'newest' | 'oldest'>('newest');
  const offlineBoards = props.offlineBoards.filter((b) => !boardHidden.has(boardKey(b)));
  const alerts = props.alerts.filter((a) => a.acknowledgedAt == null && !ackedNow.has(a.id));
  const total = alerts.length + offlineBoards.length;
  const dismissBtn = (onClick: () => void) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title="ปิดแจ้งเตือน" style={{
      background: 'transparent', border: 'none', color: 'var(--dim)', cursor: 'pointer',
      padding: 2, borderRadius: 6, display: 'inline-flex', flexShrink: 0, lineHeight: 0,
    }}><X size={15} /></button>
  );
  // Boards + alerts merged into one stream, time-sorted by the chosen direction.
  const boardNode = (b: typeof offlineBoards[number]) => {
    const never = b.status === 'never';
    const color = never ? '#64748b' : '#ef4444';
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, background: `${color}14`, border: `1px solid ${color}33` }}>
        <WifiOff size={14} color={color} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>บอร์ด {b.code} {never ? 'ไม่เคยเชื่อมต่อ' : 'ออฟไลน์'}</div>
          <div style={{ fontSize: 11, color: 'var(--dim2)', marginTop: 2, display: 'flex', gap: 8 }}>
            <span>{b.latest ? `ล่าสุด ${dayjs(b.latest).fromNow()}` : 'ยังไม่มีข้อมูล'}</span>
            <span>·</span>
            <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/devices/${b.id}?tab=energy`); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--cyan)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>ดูบอร์ด</button>
          </div>
        </div>
        {dismissBtn(() => hideBoard(boardKey(b)))}
      </div>
    );
  };
  const alertNode = (a: AlertRow) => {
    const color = a.severity === 'CRITICAL' ? 'var(--red)' : 'var(--yellow)';
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, background: a.severity === 'CRITICAL' ? 'rgba(239, 68, 68, 0.06)' : 'rgba(250, 204, 21, 0.06)', border: `1px solid ${color}33` }}>
        <AlertTriangle size={14} color={color} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{a.message}</div>
          <div style={{ fontSize: 11, color: 'var(--dim2)', marginTop: 2, display: 'flex', gap: 8 }}>
            <span>{a.code}</span>
            <span>·</span>
            <span>{dayjs(a.createdAt).fromNow()}</span>
            {a.boardId && (
              <>
                <span>·</span>
                <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/devices/${a.boardId}?tab=energy`); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--cyan)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>ดูบอร์ด</button>
              </>
            )}
          </div>
        </div>
        {dismissBtn(() => { onAcknowledge(a.id); setAckedNow((s) => new Set(s).add(a.id)); })}
      </div>
    );
  };
  const items = [
    ...offlineBoards.map((b) => ({ key: `b-${b.id}`, time: b.latest ?? 0, node: boardNode(b) })),
    ...alerts.slice(0, 50).map((a) => ({ key: `a-${a.id}`, time: new Date(a.createdAt).getTime(), node: alertNode(a) })),
  ].sort((x, y) => (sortDir === 'newest' ? y.time - x.time : x.time - y.time));
  return (
    <section className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, gap: 10,
      }}>
        <span className="card-title">แจ้งเตือน</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {total > 1 && (
            <button
              onClick={() => setSortDir((d) => (d === 'newest' ? 'oldest' : 'newest'))}
              title="สลับการเรียงลำดับ"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--bg-input)',
                border: '1px solid var(--border-color)', color: 'var(--dim)', cursor: 'pointer',
                padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              <ArrowUpDown size={12} /> {sortDir === 'newest' ? 'ล่าสุดก่อน' : 'เก่าก่อน'}
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--dim)' }}>
            {total === 0 ? 'ไม่พบ' : `${total} รายการ`}
          </span>
        </div>
      </div>
      {total === 0 ? (
        <div style={{
          flex: 1, minHeight: 120, textAlign: 'center', color: 'var(--green)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <CheckCircle2 size={22} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>ไม่พบความผิดปกติ</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto', paddingRight: 4, margin: '0 -4px 0 0' }}>
          {items.map((it) => <div key={it.key}>{it.node}</div>)}
        </div>
      )}
    </section>
  );
}

// ─── 4d. Quick Actions ─────────────────────────────────────────
function ZoneOverview(props: {
  aggs: Array<{
    id: number | null; code: string; name: string;
    boardOnline: number; boardTotal: number;
    sensorOnline: number; sensorTotal: number;
    powerNow: number; voltage: number | null; current: number | null;
    frequency: number | null; temperature: number | null;
    energyToday: number;
    worstFreshness: Freshness;
  }>;
  onJump: (zoneId: number | null) => void;
  activeZoneId: number | 'all';
}) {
  const metric = (icon: React.ReactNode, color: string, label: string, value: string, unit: string) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
      <span style={{ display: 'inline-flex', width: 26, height: 26, borderRadius: 7, alignItems: 'center', justifyContent: 'center', background: `${color}1f`, color, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: 'var(--dim)', fontSize: 10 }}>{label}</div>
        <div style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, whiteSpace: 'nowrap' }}>
          {value}{unit && <span style={{ color: 'var(--dim)', fontWeight: 400, fontSize: 10.5, marginLeft: 3 }}>{unit}</span>}
        </div>
      </div>
    </div>
  );
  const fmt = (n: number | null, d: number) => (n != null ? n.toFixed(d) : '—');
  const zoneCount = props.aggs.filter((z) => z.id != null).length;
  // Sort by zone code (natural), keeping the "ไม่ระบุโซน" bucket (id null) last.
  const sortedAggs = [...props.aggs].sort((a, b) =>
    (a.id == null) !== (b.id == null)
      ? (a.id == null ? 1 : -1)
      : a.code.localeCompare(b.code, undefined, { numeric: true }),
  );
  return (
    <section className="card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="card-title">ภาพรวมโซน</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '2px 10px', color: 'var(--dim)', fontSize: 11.5 }}>
          <Layers size={12} color="var(--cyan)" /> {zoneCount} โซน
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 12,
      }}>
        {sortedAggs.map((z) => {
          const isActive = props.activeZoneId === z.id;
          // Offline / no-data zones: fade the card and grey out the icons.
          const dim = !(z.worstFreshness === 'fresh' || z.worstFreshness === 'delayed');
          const mc = (c: string) => (dim ? '#64748b' : c);
          return (
            <div
              key={String(z.id)}
              style={{
                background: 'var(--bg-elevated)',
                border: `1px solid ${isActive ? 'var(--cyan)' : 'var(--border-color)'}`,
                borderRadius: 10, padding: '14px 16px',
                cursor: 'pointer', transition: 'border-color .15s, opacity .15s',
                opacity: dim ? 0.55 : 1,
              }}
              onClick={() => z.id != null && props.onJump(z.id)}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 10,
              }}>
                <div style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cyan)', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '1px 7px', flexShrink: 0 }}>
                    {z.code}
                  </span>
                  <span style={{
                    fontSize: 14, fontWeight: 700, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {z.name}
                  </span>
                </div>
                <Pill
                  state={z.worstFreshness}
                  label={z.worstFreshness === 'fresh' ? 'ออนไลน์'
                       : z.worstFreshness === 'delayed' ? 'หน่วง'
                       : z.worstFreshness === 'stale' ? 'หน่วง'
                       : z.worstFreshness === 'offline' ? 'ออฟไลน์' : 'ไม่มีข้อมูล'}
                />
              </div>
              {/* Electrical metrics: V/A · W/kWh · Hz/°C */}
              <div style={{
                marginTop: 12,
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 10px',
              }}>
                {metric(<Zap size={14} />, mc('#06b6d4'), 'แรงดัน', fmt(z.voltage, 1), 'V')}
                {metric(<Activity size={14} />, mc('#22c55e'), 'กระแส', fmt(z.current, 2), 'A')}
                {metric(<Gauge size={14} />, mc('#f59e0b'), 'กำลังไฟ', z.powerNow > 0 ? z.powerNow.toFixed(1) : '—', 'W')}
                {metric(<BatteryCharging size={14} />, mc('#a78bfa'), 'พลังงานวันนี้', z.energyToday > 0 ? z.energyToday.toFixed(3) : '—', 'kWh')}
                {metric(<Waves size={14} />, mc('#14b8a6'), 'ความถี่', fmt(z.frequency, 1), 'Hz')}
                {metric(<Thermometer size={14} />, mc('#f43f5e'), 'อุณหภูมิ', fmt(z.temperature, 1), '°C')}
              </div>
              {/* Boards / sensors */}
              <div style={{
                marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)',
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10,
              }}>
                {metric(<HardDrive size={14} />, mc('#a78bfa'), 'บอร์ด', `${z.boardOnline}/${z.boardTotal}`, '')}
                {metric(<Cpu size={14} />, mc('#38bdf8'), 'เซ็นเซอร์', `${z.sensorOnline}/${z.sensorTotal}`, '')}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── 6. Board Status ──────────────────────────────────────────